import React from "react";
import MetricBreakdownPopover from "./MetricBreakdownPopover";

/**
 * Delta percentage between current and previous.
 * Returns null when previous is null or 0 (UI renders "—"), avoiding
 * Infinity/NaN per spec decision D4.
 */
export const calcDeltaPct = (
  current: number,
  previous: number | null,
): number | null => {
  if (previous === null || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

const formatDeltaPct = (deltaPct: number | null): string => {
  if (deltaPct === null) return "—";
  const sign = deltaPct > 0 ? "+" : "";
  return `${sign}${deltaPct.toFixed(1)}%`;
};

interface MetricCardProps {
  label: string;
  subtitle?: string;
  value: number | null; // null → "—"
  prevValue: number | null; // null → previous period row hidden
  formatFn: (v: number) => string;
  /**
   * Concrete previous-period range (e.g. "01.04.2026 – 30.04.2026"), shown in
   * the comparison label so "vs poprzedni okres" is not ambiguous.
   */
  comparisonPeriodLabel?: string;
  /**
   * Snapshot (point-in-time) metric — a missing delta is expected rather than
   * "no data", so the delta cell reads "snapshot — brak porównania" instead
   * of a bare "—".
   */
  isSnapshot?: boolean;
  /**
   * "Lower is better" metric (e.g. churn): an INCREASE is coloured red and a
   * decrease green. Arrows stay directional (↑ for an increase). Default false
   * (higher is better).
   */
  invertDelta?: boolean;
  /**
   * Optional per-customer drill-down. When provided and `hasContent` is true, the
   * value becomes a hover/focus trigger that reveals `content` (the customer list).
   */
  breakdown?: {
    title: string;
    hasContent: boolean;
    content: React.ReactNode;
  };
}

/**
 * A single metric card. Always rendered (never conditionally) per spec.
 * Shows the current value prominently, the previous-period value, and the
 * delta (% with ↑/↓ arrow). Delta is derived here from value/prevValue.
 */
const MetricCard: React.FC<MetricCardProps> = ({
  label,
  subtitle,
  value,
  prevValue,
  formatFn,
  comparisonPeriodLabel,
  isSnapshot,
  invertDelta,
  breakdown,
}) => {
  const displayValue = value === null ? "—" : formatFn(value);
  const deltaPct = value === null ? null : calcDeltaPct(value, prevValue);

  const deltaUp = deltaPct !== null && deltaPct > 0;
  const deltaDown = deltaPct !== null && deltaPct < 0;
  // Colour reflects good/bad, not raw direction: for "lower is better" metrics
  // (churn) an increase is bad (red). Arrows below stay directional.
  const deltaIsGood = invertDelta ? deltaDown : deltaUp;
  const deltaIsBad = invertDelta ? deltaUp : deltaDown;
  // Snapshot metrics without a comparable delta: label as such so the empty
  // delta isn't mistaken for missing data.
  const snapshotNoComparison = isSnapshot && deltaPct === null;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="text-xs font-medium text-neutral-500 uppercase mb-1">
        {label}
      </div>
      {subtitle && (
        <div className="text-xs text-neutral-500 mb-2">{subtitle}</div>
      )}
      <div className="text-2xl font-semibold text-neutral-900">
        {breakdown ? (
          <MetricBreakdownPopover
            title={breakdown.title}
            hasContent={breakdown.hasContent}
            content={breakdown.content}
          >
            {displayValue}
          </MetricBreakdownPopover>
        ) : (
          displayValue
        )}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <div className="text-xs text-neutral-500">
          <span className="block">
            vs poprzedni okres
            {comparisonPeriodLabel ? ` (${comparisonPeriodLabel})` : ""}
          </span>
          <span className="text-neutral-700">
            {prevValue === null ? "—" : formatFn(prevValue)}
          </span>
        </div>
        {snapshotNoComparison ? (
          <div className="text-xs text-neutral-400 italic text-right">
            snapshot — brak porównania
          </div>
        ) : (
          <div
            className={`text-sm font-medium ${
              deltaIsGood
                ? "text-green-600"
                : deltaIsBad
                  ? "text-red-600"
                  : "text-neutral-500"
            }`}
            aria-label={
              deltaPct === null ? "brak danych do porównania" : undefined
            }
          >
            {deltaPct !== null && (
              <span aria-hidden="true" className="mr-1">
                {deltaUp ? "↑" : deltaDown ? "↓" : ""}
              </span>
            )}
            {formatDeltaPct(deltaPct)}
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricCard;
